// Blog Search Functionality
class BlogSearch {
  constructor() {
    this.searchInput = document.getElementById('blog-search-input');
    this.searchForm = this.searchInput?.closest('form');
    this.resultsContainer = document.querySelector('.blog-posts__container');
    this.originalContent = null;
    this.abortController = null;
    this.debounceTimer = null;
    
    this.init();
  }

  init() {
    if (!this.searchInput || !this.searchForm) return;
    
    // Store original content
    this.originalContent = this.resultsContainer.innerHTML;
    
    // Add event listeners
    this.searchInput.addEventListener('input', this.handleInput.bind(this));
    this.searchForm.addEventListener('submit', this.handleSubmit.bind(this));
    this.resultsContainer.addEventListener('click', this.handleResultsClick.bind(this));
    
    // Add clear functionality
    this.addClearButton();
  }

  handleResultsClick(event) {
    const paginationLink = event.target.closest('.blog-search-pagination a[href]');
    if (!paginationLink) return;

    event.preventDefault();
    this.showLoadingState();
    this.fetchSearchResults(paginationLink.href);
  }

  handleInput(event) {
    const query = event.target.value.trim();
    
    // Clear previous debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    // Abort previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    
    if (query.length === 0) {
      this.showOriginalContent();
      return;
    }
    
    // Debounce the search
    this.debounceTimer = setTimeout(() => {
      this.performSearch(query);
    }, 300);
  }

  handleSubmit(event) {
    event.preventDefault();
    const query = this.searchInput.value.trim();
    
    if (query.length === 0) return;
    
    // Perform immediate search on submit
    this.performSearch(query);
  }

  async performSearch(query) {
    this.showLoadingState();

    const searchParams = new URLSearchParams({
      q: query,
      type: 'article',
      'options[prefix]': 'last',
      view: 'blog-search-results'
    });

    const searchUrl = `${window.Shopify.routes.root}search?${searchParams.toString()}`;
    await this.fetchSearchResults(searchUrl);
  }

  async fetchSearchResults(searchUrl) {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    try {
      const url = new URL(searchUrl, window.location.origin);
      url.searchParams.set('view', 'blog-search-results');

      const response = await fetch(url.toString(), {
        signal: this.abortController.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (!response.ok) throw new Error('Search request failed');
      
      const html = await response.text();
      
      // Parse the HTML response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extract search results
      const searchResults = doc.querySelector('.blog-search-results');
      const noResults = doc.querySelector('.blog-search-no-results');
      
      if (searchResults) {
        this.showSearchResults(searchResults.outerHTML);
      } else if (noResults) {
        this.showNoResults(noResults.outerHTML);
      } else {
        this.showOriginalContent();
      }

      this.resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Blog search error:', error);
      this.showOriginalContent();
    }
  }

  showLoadingState() {
    const skeletonCard = `
      <div class="blog-search-loading__card">
        <span class="skeleton blog-search-loading__image"></span>
        <div class="v-stack gap-3">
          <span class="skeleton skeleton--text" style="--skeleton-text-width: 30%"></span>
          <span class="skeleton skeleton--text" style="--skeleton-text-width: 90%"></span>
          <span class="skeleton skeleton--text" style="--skeleton-text-width: 65%"></span>
        </div>
      </div>
    `;

    const loadingHTML = `
      <div class="blog-search-loading">
        <p class="blog-search-loading__message h3">Searching...</p>
        <div class="blog-search-loading__grid blog-posts">
          ${skeletonCard}
          ${skeletonCard}
          ${skeletonCard}
        </div>
      </div>
    `;
    
    this.resultsContainer.innerHTML = loadingHTML;
  }

  showSearchResults(resultsHTML) {
    this.resultsContainer.innerHTML = resultsHTML;
  }

  showNoResults(noResultsHTML) {
    this.resultsContainer.innerHTML = noResultsHTML;
  }

  showOriginalContent() {
    if (this.originalContent) {
      this.resultsContainer.innerHTML = this.originalContent;
    }
  }

  addClearButton() {
    // Check if clear button already exists
    const existingClearButton = document.querySelector('.blog-search-clear');
    if (existingClearButton) {
      existingClearButton.remove();
    }
    
    const clearButton = document.createElement('button');
    clearButton.type = 'button';
    clearButton.className = 'blog-search-clear';
    clearButton.innerHTML = '×';
    clearButton.style.cssText = `
      position: absolute;
      right: 60px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      font-size: 1.5rem;
      color: var(--text-subdued);
      cursor: pointer;
      padding: 0.5rem;
      display: none;
    `;
    
    clearButton.addEventListener('click', () => {
      this.searchInput.value = '';
      this.showOriginalContent();
      clearButton.style.display = 'none';
    });
    
    const searchInputContainer = this.searchInput.closest('.blog-search-input');
    searchInputContainer.style.position = 'relative';
    searchInputContainer.appendChild(clearButton);
    
    // Show/hide clear button based on input
    this.searchInput.addEventListener('input', () => {
      clearButton.style.display = this.searchInput.value.length > 0 ? 'block' : 'none';
    });
  }
}

// Initialize blog search when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new BlogSearch();
});

// Export for potential use in other scripts
window.BlogSearch = BlogSearch; 